/**
 * Actual project costs that are not labour and not material usage.
 * Rows stay in place when voided. Totals count active rows only.
 */

import { supabase } from "../supabase";
import { assertCan, CAPABILITY } from "./permissions";
import { resolveOrganizationIdForQueries } from "./orgQueryScope";
import { requireProjectFinancialView } from "./financialGuards";
import {
  assertProjectOperationallyWritable,
  OPERATIONAL_LOCK_USER_MESSAGE,
} from "./operationalLifecycle";

const PROJECT_EDIT_DENIED = "You do not have permission to edit this project.";

function requireOtherCostEdit(actor) {
  assertCan(actor, CAPABILITY.PROJECT_EDIT, {}, undefined, PROJECT_EDIT_DENIED);
}

async function requireActorOrganizationId(actor) {
  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  if (!organizationId) {
    throw new Error("Organization is required.");
  }
  return organizationId;
}

async function loadWritableProject(actor, projectId) {
  requireOtherCostEdit(actor);
  const organizationId = await requireActorOrganizationId(actor);

  const { data: project, error } = await supabase
    .from("projects")
    .select("project_id, organization_id, status, is_active, client_id")
    .eq("project_id", projectId)
    .eq("organization_id", organizationId)
    .maybeSingle();

  if (error) throw error;
  if (!project || project.organization_id !== organizationId) {
    throw new Error("Project not found.");
  }

  let client = null;
  if (project.client_id) {
    const { data, error: clientError } = await supabase
      .from("clients")
      .select("client_id, archived_at, client_status, organization_id")
      .eq("client_id", project.client_id)
      .eq("organization_id", organizationId)
      .maybeSingle();

    if (clientError) throw clientError;
    if (!data || data.organization_id !== organizationId) {
      throw new Error(OPERATIONAL_LOCK_USER_MESSAGE);
    }
    client = data;
  }

  assertProjectOperationallyWritable(project, client);
  return { organizationId };
}

export function sumOtherCostAmounts(rows = []) {
  return (rows || []).reduce((sum, row) => {
    if (row?.is_active === false) return sum;
    const amount = Number(row?.amount);
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
}

export async function loadProjectOtherCosts(actor, projectId) {
  requireProjectFinancialView(actor);
  const organizationId = await requireActorOrganizationId(actor);

  const { data, error } = await supabase
    .from("project_other_costs")
    .select("*")
    .eq("organization_id", organizationId)
    .eq("project_id", projectId)
    .eq("is_active", true)
    .order("created_at", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function addProjectOtherCost(actor, projectId, { description, amount, note }) {
  const { organizationId } = await loadWritableProject(actor, projectId);
  const trimmed = String(description || "").trim();
  const value = Number(amount);

  if (!trimmed) {
    throw new Error("Enter a description.");
  }
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error("Enter an amount greater than zero.");
  }

  const { data, error } = await supabase
    .from("project_other_costs")
    .insert({
      organization_id: organizationId,
      project_id: projectId,
      description: trimmed,
      amount: Math.round(value * 100) / 100,
      note: String(note || "").trim() || null,
      is_active: true,
      created_by: actor?.supabase_user_id || null,
    })
    .select("*")
    .single();

  if (error) throw error;
  return data;
}

export async function voidProjectOtherCost(actor, otherCostId) {
  requireOtherCostEdit(actor);
  const organizationId = await requireActorOrganizationId(actor);

  const { data: row, error: loadError } = await supabase
    .from("project_other_costs")
    .select("other_cost_id, project_id, organization_id, is_active")
    .eq("other_cost_id", otherCostId)
    .eq("organization_id", organizationId)
    .maybeSingle();

  if (loadError) throw loadError;
  if (!row) {
    throw new Error("That cost is not in this organization.");
  }

  await loadWritableProject(actor, row.project_id);

  const { error } = await supabase
    .from("project_other_costs")
    .update({
      is_active: false,
      voided_at: new Date().toISOString(),
      voided_by: actor?.supabase_user_id || null,
    })
    .eq("other_cost_id", otherCostId)
    .eq("organization_id", organizationId)
    .eq("is_active", true);

  if (error) throw error;
}
